// Author: Axel Antoine
// mail: ax.antoine@gmail.com
// website: https://axantoine.com
// 23/02/2021

// Loki, Inria project-team with Université de Lille
// within the Joint Research Unit UMR 9189 CNRS-Centrale
// Lille-Université de Lille, CRIStAL.
// https://loki.lille.inria.fr

// LICENCE: Licence.md

import { BufferGeometry, Vector3, Uint32BufferAttribute, Float32BufferAttribute } from 'three';
import { Face } from './Face';
import { Vertex } from './Vertex';
import { Halfedge } from './Halfedge';
import { addEdge } from '../operations/addEdge';
import { addFace } from '../operations/addFace';
import { addVertex } from '../operations/addVertex';
import { removeVertex } from '../operations/removeVertex';
import { removeEdge } from '../operations/removeEdge';
import { removeFace } from '../operations/removeFace';
import { cutFace } from '../operations/cutFace';
import { splitEdge } from '../operations/splitEdge';
import { setFromGeometry } from '../operations/setFromGeometry';
import VerticesToFaceMap from './VerticesToFaceMap';
import { cutEdges, EdgeIntersection, MultiCutOptions } from '../operations/cutEdges';
import { connectVertexArrays } from '../operations/connectVertices';
import { bridgeSegments } from '../operations/bridgeSegments';
import edgeCollapse from '../operations/edgeCollapse';
import sculpt, { SculptOpts } from '../operations/sculpt';
import { connectRing } from '../operations/connectRing';
import { reduceBorder } from '../operations/reduceBorder';
import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh';
import { createFace } from '../operations/createFace';
import { createQuadFace } from '../operations/createQuadFace';
import {
  alignVerticesToAxis as alignVerticesToAxisOp,
  AlignVerticesToAxisOptions,
  Axis as AlignAxis,
} from '../operations/alignVerticesToAxis';

import { TrackedArray } from './TrackedArray';

export interface SubGeometry {
  newGeometry: BufferGeometry;
  oldFaces: Face[];
}

/**
 * Class representing an Halfedge Data Structure
 */
export class HalfedgeDS {
  /** @readonly Faces
   *  @warning You cannot use faceIndex from three.js, because three.js automatically modifies the indexes
   *
   *
   * */
  readonly faces = new TrackedArray<Face>();
  /** @readonly Vertices */
  readonly vertices = new TrackedArray<Vertex>();
  /** @readonly Halfedges */
  readonly halfedges = new TrackedArray<Halfedge>();

  readonly vertexToRenderId = new Map<Vertex, number>();

  readonly renderIdToVertex = new Map<number, Vertex>();

  readonly idToVertex = new Map<number, Vertex>();

  readonly boundaryLoops: Vertex[][] = [];

  readonly verticesToFace = new VerticesToFaceMap<Face>();

  private nextRenderId = 0;

  /**
   * Sets the halfedge structure from a BufferGeometry.
   * @param geometry BufferGeometry to read
   * @param tolerance Tolerance distance from which positions are considered equal
   */
  setFromGeometry(geometry: BufferGeometry, tolerance = 1e-10) {
    this.clear();
    const indexVertexArray = setFromGeometry(this, geometry, tolerance);

    for (const vertex of this.vertices) {
      this.vertexToRenderId.set(vertex, vertex.renderId);
      this.renderIdToVertex.set(vertex.renderId, vertex);
      this.idToVertex.set(vertex.id, vertex);
      if (vertex.renderId > this.nextRenderId) {
        this.nextRenderId = vertex.renderId + 1;
      }
    }
    for (const face of this.faces) {
      const faceVertices = face.getVertices().map((v) => v.id);
      this.verticesToFace.set(faceVertices[0], faceVertices[1], faceVertices[2], face);
    }
    this.boundaryLoops.push(...this.calculateBoundaryLoops());
    return indexVertexArray;
  }

  public toBufferGeometry(): BufferGeometry {
      const geometry = new BufferGeometry();
  
      // Create vertex index mapping
      const vertexToIndex = new Map<Vertex, number>();
      const positions: number[] = [];
      const colors: number[] = [];
      const indices: number[] = [];
  
      // Add all vertices to the geometry
      let vertexIndex = 0;
      for (const vertex of this.vertices) {
        vertexToIndex.set(vertex, vertexIndex);
  
        // Add position
        positions.push(vertex.position.x, vertex.position.y, vertex.position.z);
  
        // Add color (use vertex color if available, otherwise white)
        if (vertex.color) {
          colors.push(vertex.color.x, vertex.color.y, vertex.color.z);
        } else {
          colors.push(1.0, 1.0, 1.0); // Default white
        }
  
        vertexIndex++;
      }
  
      // Add all faces to the geometry
      for (const face of this.faces) {
        const vertices = face.getVertices();
        if (vertices.length === 3) {
          // Triangle face
          const a = vertexToIndex.get(vertices[0])!;
          const b = vertexToIndex.get(vertices[1])!;
          const c = vertexToIndex.get(vertices[2])!;
          indices.push(a, b, c);
        }
      }
  
      // Set attributes
      geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
  
      // Compute normals and other properties
      geometry.computeVertexNormals();
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
  
      return geometry;
    }

  public getMeanEdgeLength(): number {
    let totalLength = 0;
    let edgeCount = 0;

    for (const halfedge of this.halfedges) {
      const length = halfedge.vertex.position.distanceTo(halfedge.next.vertex.position);
      if (length > 0) {
        totalLength += length;
        edgeCount++;
      }
    }

    return edgeCount > 0 ? totalLength / edgeCount : 0;
  }

  /**
   * Returns an array of all the halfedge loops in the structure.
   *
   * *Note: Actually returns an array of halfedges from which loop generator
   * can be called*
   *
   * @returns
   */
  loops() {
    const loops = new Array<Halfedge>();

    const handled = new Set<Halfedge>();

    for (const halfedge of this.halfedges) {
      if (!handled.has(halfedge)) {
        for (const he of halfedge.nextLoop()) {
          handled.add(he);
        }
        loops.push(halfedge);
      }
    }
    return loops;
  }

  /**
   * Clear the structure data
   */
  clear() {
    this.faces.clear();
    this.vertices.clear();
    this.halfedges.clear();
    this.vertexToRenderId.clear();
    this.renderIdToVertex.clear();
    this.idToVertex.clear();
    this.boundaryLoops.clear();
    this.verticesToFace.clear();
    this.nextRenderId = 0;
  }

  /**
   * Adds a new vertex to the structure at the given position and returns it.
   * If checkDuplicates is true, returns any existing vertex that matches the
   * given position.
   *
   * @param position New vertex position
   * @param checkDuplicates Enable/disable existing vertex matching, default false
   * @param tolerance Tolerance used for vertices position comparison
   * @param renderId Original vertex id in three
   * @returns
   */
  public addVertex(position: Vector3, renderId?: number, color?: Vector3, checkDuplicates = false, tolerance = 1e-10): Vertex {
    if (renderId === undefined) {
      renderId = this.nextRenderId;
    }
    if (color === undefined) {
      color = new Vector3(1, 1, 1);
    }
    const newVertex = addVertex(this, position, checkDuplicates, renderId, tolerance, color);
    this.vertexToRenderId.set(newVertex, newVertex.renderId);
    this.renderIdToVertex.set(newVertex.renderId, newVertex);
    this.idToVertex.set(newVertex.id, newVertex);
    if (newVertex.renderId >= this.nextRenderId) {
      this.nextRenderId = newVertex.renderId + 1;
    }
    return newVertex;
  }

  /**
   * Adds an edge (i.e. a pair of halfedges) between the given vertices.
   * Requires vertices to be free, i.e., there is at least one free halfedge
   * (i.e. without face) in their neighborhood.
   *
   * @param v1 First vertex to link
   * @param v2 Second vertex to link
   * @param allowParallels Allows multiple pair of halfedges between vertices, default false
   * @returns Existing or new halfedge
   */
  addEdge(v1: Vertex, v2: Vertex, allowParallels = false) {
    return addEdge(this, v1, v2, allowParallels);
  }

  /**
   * Adds a face to an existing halfedge loop
   * @param halfedge
   * @returns
   */
  addFace(halfedges: Halfedge[]) {
    return addFace(this, halfedges);
  }

  createFace(v1: Vertex, v2: Vertex, v3: Vertex) {
    return createFace(this, v1, v2, v3);
  }

  createQuadFace(v1: Vertex, v2: Vertex, v3: Vertex, v4: Vertex) {
    return createQuadFace(this, v1, v2, v3, v4);
  }

  connectRing(verts: Vertex[], wrap: boolean) {
    return connectRing(this, verts, wrap);
  }

  /**
   * Removes a vertex from the structure
   * @param vertex Vertex to remove
   * @param mergeFaces If true, merges connected faces if any, otherwise removes them. Default true
   */
  removeVertex(vertex: Vertex, mergeFaces = true) {
    removeVertex(this, vertex, mergeFaces);
    this.vertexToRenderId.delete(vertex);
    this.renderIdToVertex.delete(vertex.renderId);
    this.idToVertex.delete(vertex.id);
  }

  /**
   * Removes an edge from the structrure
   * @param halfedge Halfedge to remove
   * @param mergeFaces If true, merges connected faces if any, otherwise removes them. Default true
   */
  removeEdge(halfedge: Halfedge, mergeFaces = true) {
    return removeEdge(this, halfedge, mergeFaces);
  }

  /**
   * Removes a face from the structure.
   * @param face Face to remove
   */
  removeFace(face: Face) {
    return removeFace(this, face);
  }

  /**ts
   * Cuts the `face` between the vertices `v1` and `v2`.
   * v1 and v2 must either be vertices of the face or isolated vertices.
   *
   * To test if a new face is created, simply do
   * ```
   *    const halfedge = struct.cutFace(face, v1, v2, true);
   *    if (halfedge.face !== halfedge.twin.face) {
   *      // Halfedge are on different faces / loops
   *      const existingFace = halfedge.face;
   *      const newFace = halfedge.twin.face;
   *    }
   * ```
   *
   *
   * @param face Face to cut
   * @param v1 1st vertex
   * @param v2 2nd vertex
   * @param createNewFace wether to create a new face or not when cutting
   * @returns the cutting halfedge
   */
  cutFace(face: Face, v1: Vertex, v2: Vertex, createNewFace = true) {
    return cutFace(this, face, v1, v2, createNewFace);
  }

  /**
   * Splits the halfedge at position and returns the new vertex
   * @param halfEdge The HalfEdge to be splitted
   * @param position Position of the split vertex
   * @returns the new created vertex
   */
  splitEdge(halfedge: Halfedge, position: Vector3, tolerance = 0.08) {
    return splitEdge(this, halfedge, position, tolerance);
  }
  /**
   * Merges the source vertex into the target vertex, moving the target to the
   * midpoint of the source and target positions.
   *
   * @param source Vertex to collapse
   * @param target Vertex to merge into
   * @param moveVertexToMidpoint If true, moves the target vertex to the midpoint of source and target. Default true
   */

  edgeCollapse(source: Vertex, target: Vertex, moveVertexToMidpoint = true, debug = false) {
    return edgeCollapse(this, source, target, moveVertexToMidpoint, debug);
  }

  /**
   * Cuts the edges at the intersection points.
   * @param edgeIntersections Array of edge intersections
   * @param opts Options for the cut
   */
  cutEdges(edgeIntersections: EdgeIntersection[], opts: MultiCutOptions = {}) {
    return cutEdges(this, edgeIntersections, opts);
  }

  /**  Connects an array of vertices into a continuous path
   * @param vertices Array of vertices to connect
   */
  connectVertices(vertices: Array<Vertex>) {
    return connectVertexArrays(this, vertices);
  }

  /** Bridges two vertex paths with faces
   * @param pathA First path of vertices
   * @param pathB Second path of vertices
   */

  bridgeSegments(pathA: Vertex[], pathB: Vertex[]) {
    return bridgeSegments(this, pathA, pathB);
  }

  alignVerticesToAxis(vertices: Iterable<Vertex>, axis: AlignAxis, options: AlignVerticesToAxisOptions = {}): number {
    return alignVerticesToAxisOp(vertices, axis, options);
  }

  sculpt(centers: Vertex[], opts: SculptOpts = {}): number {
    return sculpt(centers, opts);
  }

  reduceBorder(reduceCount: number, mesh: HalfEdgeMesh): Promise<void> {
    return reduceBorder(mesh, reduceCount);
  }

  /**
   * Generates a BufferGeometry from the structure by excluding the faces in
   * `excludedFaces`. The result is a new geometry and a list of the faces that
   * were included in the new geometry.
   *
   * @param excludedFaces Set of faces to exclude
   * @returns an object with the new geometry and the old included faces
   */
  excludedBufferGeometry(excludedFaces: Set<Face>): SubGeometry {
    const geometry = new BufferGeometry();

    const vertexIndexMap = new Map<Vertex, number>();
    const positions = new Float32Array(this.vertices.length * 3);
    const colors = new Float32Array(this.vertices.length * 3);
    const indices: number[] = [];
    const oldFaces: Face[] = [];
    const newVertices: Set<Vertex> = new Set();
    const newFaces: Set<Face> = new Set();

    let vertexIndex = 0;

    for (const face of this.faces) {
      if (excludedFaces.has(face)) {
        continue;
      }
      newFaces.add(face);
      newVertices.add(face.halfedge.vertex);
      newVertices.add(face.halfedge.next.vertex);
      newVertices.add(face.halfedge.prev.vertex);
    }

    for (const vertex of newVertices) {
      vertexIndexMap.set(vertex, vertexIndex);
      positions[vertexIndex * 3] = vertex.position.x;
      positions[vertexIndex * 3 + 1] = vertex.position.y;
      positions[vertexIndex * 3 + 2] = vertex.position.z;
      colors[vertexIndex * 3] = vertex.color.x;
      colors[vertexIndex * 3 + 1] = vertex.color.y;
      colors[vertexIndex * 3 + 2] = vertex.color.z;
      vertexIndex++;
    }

    for (const face of newFaces) {
      indices.push(vertexIndexMap.get(face.halfedge.vertex)!);
      indices.push(vertexIndexMap.get(face.halfedge.next.vertex)!);
      indices.push(vertexIndexMap.get(face.halfedge.prev.vertex)!);
      oldFaces.push(face);
    }

    // Assign attributes to the geometry
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    geometry.setIndex(new Uint32BufferAttribute(indices, 1));

    return {
      newGeometry: geometry,
      oldFaces: oldFaces,
    };
  }

  /**
   * Generates a BufferGeometry from the structure by only including the
   * vertices in `include`. The result is a new geometry and a list of the
   * faces that were included in the new geometry.
   *
   * @param include Set of vertices to include
   * @returns an object with the new geometry and the old included faces
   */
  includedBufferGeometry(include: Set<Vertex>): SubGeometry {
    const geometry = new BufferGeometry();

    const vertexIndexMap = new Map<Vertex, number>();
    const positions = new Float32Array(include.size * 3);
    const colors = new Float32Array(include.size * 3);
    const indices: number[] = [];
    const oldFaces: Face[] = [];

    let vertexIndex = 0;

    for (let i = 0; i < this.vertices.length; i++) {
      const vertex = this.vertices[i];

      if (include.has(vertex)) {
        positions[vertexIndex * 3] = vertex.position.x;
        positions[vertexIndex * 3 + 1] = vertex.position.y;
        positions[vertexIndex * 3 + 2] = vertex.position.z;

        colors[vertexIndex * 3] = vertex.color.x;
        colors[vertexIndex * 3 + 1] = vertex.color.y;
        colors[vertexIndex * 3 + 2] = vertex.color.z;

        vertexIndexMap.set(vertex, vertexIndex);

        vertexIndex++;
      }
    }

    for (const face of this.faces) {
      const halfedge = face.halfedge;
      const newIndices: number[] = [];
      let edge = halfedge;
      let guard = 0;
      do {
        if (include.has(edge.vertex)) {
          newIndices.push(vertexIndexMap.get(edge.vertex)!);
        } else {
          break;
        }
        edge = edge.next;
      } while (edge !== halfedge && ++guard < 8); // guard against corrupt rings

      if (newIndices.length === 3) {
        indices.push(...newIndices);
        oldFaces.push(face);
      }
    }

    // Assign attributes to the geometry
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    geometry.setIndex(new Uint32BufferAttribute(indices, 1));

    return {
      newGeometry: geometry,
      oldFaces: oldFaces,
    };
  }

  private calculateBoundaryLoops(): Vertex[][] {
    const closed: Vertex[][] = [];
    const open: Vertex[][] = [];
    const visitedHE = new Set<Halfedge>();
  
    const isBoundary = (h?: Halfedge | null) => !!h && !h.face;
    const stepForward = (h: Halfedge) => {
      const p = h.prev;
      const t = p?.twin ?? null;
      return isBoundary(t) ? t! : null;
    };
    const stepBackward = (h: Halfedge) => {
      const n = h.twin?.next ?? null;
      return isBoundary(n) ? n! : null;
    };
  
    for (const seed of this.halfedges) {
      if (!isBoundary(seed) || visitedHE.has(seed)) continue;
  
      const forwardVerts: Vertex[] = [];
      let heF: Halfedge | null = seed;
      let isClosed = false;
      while (heF && !visitedHE.has(heF)) {
        visitedHE.add(heF);
        forwardVerts.push(heF.vertex);
        heF = stepForward(heF);
        if (heF === seed) { isClosed = true; break; }
      }
  
      if (isClosed) {
        closed.push(forwardVerts);
        continue;
      }

      const backwardVerts: Vertex[] = [];
      let heB: Halfedge | null = stepBackward(seed);
      while (heB && !visitedHE.has(heB)) {
        visitedHE.add(heB);
        backwardVerts.push(heB.vertex);
        heB = stepBackward(heB);
      }
  
      backwardVerts.reverse();
      const chain = backwardVerts.concat(forwardVerts);
      if (chain.length > 0) open.push(chain);
    }
  
    return closed.concat(open);
  }

  public assertHalfedgeInvariant(label = '') {
  const err = (m: string) => { throw new Error(`[HE-INV ${label}] ${m}`) }

  // 1) halfedge páry
  for (const he of this.halfedges) {
    if (!he.twin) err('missing twin');
    if (he.twin?.twin !== he) err('twin.twin !== self');
    if (!he.next || !he.prev) err('missing next/prev');
    if (he.next?.prev !== he) err('next.prev !== self');
    if (he.prev?.next !== he) err('prev.next !== self');
  }

  // 2) face cykly (3-úhelník)
  for (const f of this.faces) {
    if (!f.halfedge) err('face without halfedge');
    const a = f.halfedge;
    const b = a.next;
    const c = b.next;
    if (c.next !== a) err('face loop not closed');
    if (a.face !== f || b.face !== f || c.face !== f) err('halfedge.face mismatch');
    if (new Set([a.vertex, b.vertex, c.vertex]).size !== 3) err('degenerate triangle');
  }

  // 3) twin face nesmí být stejný (u vnitřních)
  for (const he of this.halfedges) {
    if (he.face && he.twin?.face && he.face === he.twin.face) {
      err('both sides same face');
    }
  }

  // 4) mapy
  for (const v of this.vertices) {
    if (this.renderIdToVertex.get(v.renderId) !== v) err('renderId map mismatch');
    if (this.idToVertex.get(v.id) !== v) err('id map mismatch');
  }
  }

  public getNextRenderId(): number {
    return this.nextRenderId;
  }
}
